import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { toast } from 'react-toastify';
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Paper,
  TextField,
  TableBody,
  FormControl,
  MenuItem,
  InputLabel,
  Select,
  Button,
  IconButton,
  Typography,
  FormHelperText,
  Box,
} from '@mui/material';
import projectsApi from '@api/projects.api';
import Modal from '@components/Modal';
import Loader from '@components/Loader';
import Icon from '@components/Icon';
import stores from '@stores';

import { validationMap } from './adminProjects.helper';
import { StyledName } from './styles';

const AdminProjects = () => {
  const [projects, setProjects] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [project, setProject] = useState({});
  const [validate, setValidate] = useState(false);

  const {
    layoutStore: { setConfirmModal },
  } = stores;

  const fetchList = async () => {
    setLoading(true);
    try {
      const data = await projectsApi.fetch();
      await setProjects(data);
      setLoading(false);
    } catch (err) {
      toast.error(err?.message);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = (row) => {
    setProject(row);
    setOpenModal(true);
  };

  const handleChange = (obj) => {
    setProject({ ...project, ...obj });
  };

  const handleDelete = async (id, name) => {
    setConfirmModal({
      type: 'warning',
      open: true,
      title: `Do you really want to delete project ${name} ?`,
      positiveCallback: async () => {
        await projectsApi.delete(id);
        fetchList();
      },
      positiveButtonText: 'Ok',
      negativeButtonText: 'Cancel',
    });
  };

  const validator = (key: string, passValidate = false): string => {
    if (
      (validate || passValidate) &&
      validationMap[key]?.isError(project[key])
    ) {
      return validationMap[key]?.helperText;
    }
    return '';
  };

  useEffect(() => {
    fetchList();
  }, []);

  const handleSubmit = async () => {
    setValidate(true);

    if (!validator('title', true) && !validator('type', true)) {
      setValidate(false);
      if (project?._id) {
        await projectsApi.edit(project, project._id);
      } else {
        await projectsApi.add(project);
      }
      fetchList();
      setOpenModal(false);
    }
  };

  return (
    <div>
      <Box
        sx={{
          textAlign: 'right',
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h2" component="h2">
          Projects
        </Typography>
        <Button
          sx={{ pl: 1, pr: 1 }}
          variant="contained"
          onClick={() => handleClick({})}
          size="large"
        >
          Projekt hinzufügen
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell>Updated At</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {projects.map((row) => (
              <TableRow
                key={row._id}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell>
                  <StyledName onClick={() => handleClick(row)}>
                    {row.title}
                  </StyledName>
                </TableCell>
                <TableCell>{row.type}</TableCell>
                <TableCell>{row.createdAt}</TableCell>
                <TableCell>{row.updatedAt}</TableCell>
                <TableCell style={{ width: 0 }}>
                  <IconButton onClick={() => handleDelete(row._id, row.title)}>
                    <Icon icon="trash" size={21} color="#778097" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Modal
        onConfirm={() => handleSubmit()}
        title={project?._id ? `Edit ${project.title}` : 'New Project'}
        modalOpen={openModal}
        onClose={() => setOpenModal(false)}
      >
        <FormControl
          variant="standard"
          sx={{ mb: 1, width: '100%' }}
          error={!!validator('type')}
        >
          <InputLabel>Type</InputLabel>
          <Select
            value={project?.type || ''}
            onChange={(e) => handleChange({ type: e.target.value })}
            label="Type"
          >
            <MenuItem value="Nursery">Nursery</MenuItem>
            <MenuItem value="School">School</MenuItem>
          </Select>
          <FormHelperText>{validator('type')}</FormHelperText>
        </FormControl>

        <TextField
          sx={{ width: '100%' }}
          label="Title"
          error={!!validator('title')}
          helperText={validator('title')}
          defaultValue={project?.title || ''}
          variant="standard"
          onChange={(e) => handleChange({ title: e.target.value })}
        />
      </Modal>

      {loading && <Loader filled />}
    </div>
  );
};

export default observer(AdminProjects);
